// Ported from the vouchers admin (components/common/save-bar.tsx): the create
// page's save bar, anchored to the bottom of the content column.

import { Button } from "@pej/kore/components";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export interface PageSaveBarProps {
    dirty: boolean;
    saving?: boolean;
    onCancel: () => void;
    onSave: () => void;
    saveLabel?: string;
    cancelLabel?: string;
    /** Shown while dirty instead of "Unsaved changes.". */
    status?: ReactNode;
    /** Shown instead of the status, as an alert. */
    error?: string;
    /** E.g. Discard with nothing to discard. */
    cancelDisabled?: boolean;
    className?: string;
}

/**
 * Save bar anchored to the bottom of the main content column. Treated as
 * page chrome, not a form card: translucent page-coloured background with
 * backdrop-blur, a border-top horizon, and an upward shadow lift it above the
 * form content. It starts at the sidebar's right edge (`--sidebar-w`, which
 * the sidebar writes as it collapses), so the sidebar stays a stable
 * navigation landmark. Always visible, so Cancel is reachable at any scroll
 * position; Save is disabled until the form is dirty.
 *
 * Kept local rather than kore `SaveBar`, for the same reason vouchers gives:
 * kore's is a floating pill whose inner chrome isn't className-exposed, so it
 * can't be made full-width page chrome without a kore change. The section
 * pages' pb-24 leaves clearance for it.
 */
export function PageSaveBar({
    dirty,
    saving = false,
    onCancel,
    onSave,
    saveLabel = "Save changes",
    cancelLabel = "Cancel",
    status,
    error,
    cancelDisabled = false,
    className,
}: PageSaveBarProps) {
    return (
        <section
            aria-label="Form actions"
            className={cn(
                // `transition-[left] duration-200` matches the sidebar's own
                // width transition so both slide as one. The upward halo uses
                // --border so it still shows against a dark canvas.
                "fixed bottom-0 right-0 left-[var(--sidebar-w)] z-30 h-14 border-t border-border bg-background/85 shadow-[0_-8px_24px_-8px_var(--border)] backdrop-blur-md transition-[left] duration-200 ease-out",
                className,
            )}
        >
            <div className="flex h-full w-full items-center gap-3 px-6">
                {/* Status on the left as text, Cancel and Save together on the right. */}
                {error ? (
                    <span role="alert" className="text-sm text-kore-danger-text">
                        {error}
                    </span>
                ) : (
                    dirty && <span className="text-sm text-muted-foreground">{status ?? "Unsaved changes."}</span>
                )}
                <div className="flex-1" />
                <Button type="button" variant="outline" size="sm" onClick={onCancel} disabled={cancelDisabled}>
                    {cancelLabel}
                </Button>
                <Button type="button" size="sm" onClick={onSave} disabled={!dirty || saving}>
                    {saving ? "Saving…" : saveLabel}
                </Button>
            </div>
        </section>
    );
}
